import { Checkbox, RadioGroup, type RadioGroupRenderItem } from "@synthesis/shared-ui";
import { useFormContext, useWatch } from "react-hook-form";

import type { AdjacentObjectDataFormValues } from "../../create-application-page.lib";
import { CreateApplicationInfoMark } from "../create-application-info-mark";

type AdjacentObjectDataBooleanField = "guaranteeLettersRequired" | "licensedSubsoilDevelopment" | "hasDesignDocumentationExpertise";

type AdjacentObjectDataRadioField = "cadastralDataAvailability" | "applicantIsCargoSender";

type AdjacentObjectDataOption = {
  value: string;
  label: string;
};

type AdjacentObjectDataCheckboxProps = {
  name: AdjacentObjectDataBooleanField;
  label: string;
  hint?: string;
  onCheckedChange?: (checked: boolean) => void;
};

export const AdjacentObjectDataCheckbox = ({ name, label, hint, onCheckedChange }: AdjacentObjectDataCheckboxProps) => {
  const methods = useFormContext<AdjacentObjectDataFormValues>();
  const checked = useWatch({ name });

  const handleChange = (value: boolean | "indeterminate" | undefined) => {
    const nextValue = value === true;
    methods.setValue(name, nextValue, { shouldValidate: true });
    onCheckedChange?.(nextValue);
  };

  return (
    <div className="flex items-start gap-2">
      <Checkbox checked={checked} label={label} onChange={handleChange} />
      {hint && <CreateApplicationInfoMark description={hint} arrowSide="right" maxWidth="310px" className="mt-0.5" />}
    </div>
  );
};

type AdjacentObjectDataRadioGroupProps = {
  name: AdjacentObjectDataRadioField;
  options: AdjacentObjectDataOption[];
  horizontal?: boolean;
  onChange?: (value: string) => void;
};

export const AdjacentObjectDataRadioGroup = ({ name, options, horizontal = false, onChange }: AdjacentObjectDataRadioGroupProps) => {
  const items: RadioGroupRenderItem[] = options.map((option) => ({
    id: `${name}-${option.value}`,
    value: option.value,
    label: option.label,
  }));

  const handleChange = (value: string) => {
    onChange?.(value);
  };

  return <RadioGroup name={name} renderRadioGroupElements={items} horizontal={horizontal} onClick={handleChange} />;
};
